import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import QRCode from 'qrcode';
import { zodResolver } from '../lib/zod-resolver';
import { enableTwoFactor, verifyTotp, type EnableTwoFactorResult } from '../lib/auth';
import { useSession } from '../lib/session';
import { API_BASE, useTRPC } from '../lib/trpc';
import classes from './LoginPage.module.css';

const passwordSchema = z.object({ password: z.string().min(1, 'Введіть пароль') });
const codeSchema = z.object({ code: z.string().regex(/^\d{6}$/u, 'Код складається з 6 цифр') });

/** `otpauth://totp/Label:email?secret=…&issuer=…` — the manual-entry fallback next to the QR. */
function secretFromUri(uri: string): string {
  try {
    return new URL(uri).searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
}

/**
 * Forced TOTP enrolment for `super_admin` accounts that have not set up a second factor yet.
 *
 * The secret is handed out only after the operator re-proves who they are — with the
 * account's password, or, for an account that has none because it signs in only through the
 * corporate SSO, on the strength of the SSO sign-in it just completed (a short-lived, one-use
 * grant the API keeps for this session; see `apps/api/src/auth/sso-enrolment.ts`). Which of
 * the two the page asks for comes from `session.me.hasPassword`, never from a guess: an
 * SSO-only admin must never be shown a password field that no input can satisfy — that is
 * exactly the dead end this page had in production on 2026-09-01.
 *
 * The QR and backup codes stay on screen through the whole code-entry step — including a
 * wrong attempt — on purpose: re-fetching `/two-factor/enable` a second time would mint a
 * new secret and invalidate the one the operator just scanned into their app.
 */
export function TotpSetupPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const session = useSession();

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [totpURI, setTotpURI] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [codesSaved, setCodesSaved] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);
  /** Set once the SSO grant was refused: the only way forward is a fresh SSO sign-in. */
  const [ssoReauth, setSsoReauth] = useState(false);

  const passwordForm = useForm({
    initialValues: { password: '' },
    validate: zodResolver(passwordSchema),
  });
  const codeForm = useForm({
    initialValues: { code: '' },
    validate: zodResolver(codeSchema),
  });

  const showSecret = async (result: EnableTwoFactorResult): Promise<boolean> => {
    if (!result.ok || result.totpURI === undefined) return false;
    setTotpURI(result.totpURI);
    setBackupCodes(result.backupCodes ?? []);
    setQrDataUrl(await QRCode.toDataURL(result.totpURI));
    return true;
  };

  const submitPassword = passwordForm.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      if (!(await showSecret(await enableTwoFactor(values.password)))) {
        setError('Невірний пароль.');
      }
    } finally {
      setBusy(false);
    }
  });

  const continueWithSso = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setSsoReauth(false);
    try {
      const result = await enableTwoFactor();
      if (await showSecret(result)) return;
      /**
       * Either the grant from the last SSO sign-in was already used or has expired (the
       * API says so explicitly), or the request failed for some other reason — in both
       * cases a fresh SSO sign-in is the actionable next step, so it is offered for both.
       */
      setSsoReauth(true);
      setError(
        result.code === 'SSO_STEP_UP_REQUIRED'
          ? 'Підтвердження SSO застаріло або вже використане. Увійдіть через SSO ще раз, щоб отримати секрет.'
          : 'Не вдалося отримати секрет. Спробуйте ще раз або увійдіть через SSO повторно.',
      );
    } finally {
      setBusy(false);
    }
  };

  const submitCode = codeForm.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      const result = await verifyTotp(values.code);
      if (!result.ok) {
        setError(result.error ?? 'Невірний код.');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: trpc.session.me.queryKey() });
      await navigate({ to: '/' });
    } finally {
      setBusy(false);
    }
  });

  const errorLine = error ? (
    <Text className={classes.error} data-testid="totp-error" role="alert">
      {error}
    </Text>
  ) : null;

  const confirmation =
    session === null ? (
      <Loader size="sm" data-testid="totp-loading" />
    ) : session.hasPassword ? (
      <form onSubmit={submitPassword} noValidate>
        <Stack gap="sm">
          <Text size="sm">
            Підтвердіть пароль, щоб отримати секрет для застосунку автентифікації.
          </Text>
          <PasswordInput
            label="Пароль"
            autoComplete="current-password"
            data-testid="totp-password"
            {...passwordForm.getInputProps('password')}
          />
          {errorLine}
          <Button type="submit" loading={busy} fullWidth data-testid="totp-password-submit">
            Продовжити
          </Button>
        </Stack>
      </form>
    ) : (
      <Stack gap="sm">
        <Text size="sm">
          Ви увійшли через корпоративний SSO — локального пароля в цього облікового запису немає.
          Секрет для застосунку автентифікації видається на підставі щойно пройденого SSO-входу.
        </Text>
        {errorLine}
        {ssoReauth ? (
          <Anchor href={`${API_BASE}/api/auth/sso/start`} size="sm" data-testid="totp-sso-reauth">
            Увійти через SSO ще раз
          </Anchor>
        ) : null}
        <Button
          loading={busy}
          fullWidth
          data-testid="totp-sso-continue"
          onClick={() => {
            void continueWithSso();
          }}
        >
          Продовжити через SSO
        </Button>
      </Stack>
    );

  return (
    <Box className={classes.root}>
      <Box className={classes.card} data-testid="totp-setup-card">
        <Stack gap={4} mb="lg">
          <Box className={classes.logo}>S</Box>
          <Text className={classes.title}>Двофакторна автентифікація</Text>
          <Text className={classes.subtitle}>обовʼязково для ролі super_admin</Text>
        </Stack>

        {totpURI === null ? (
          confirmation
        ) : (
          <Stack gap="sm">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="QR-код для застосунку автентифікації"
                width={200}
                height={200}
                data-testid="totp-qr"
              />
            ) : null}
            <Text size="xs">
              Секрет для ручного введення:{' '}
              <span data-testid="totp-secret">{secretFromUri(totpURI)}</span>
            </Text>
            <Text size="xs" fw={600}>
              Резервні коди — кожен працює один раз, збережіть їх окремо:
            </Text>
            <Box data-testid="totp-backup-codes">
              {backupCodes.map((code) => (
                <Text key={code} size="xs" className={classes.subtitle}>
                  {code}
                </Text>
              ))}
            </Box>

            {!showCodeForm ? (
              <>
                <Checkbox
                  label="Я зберіг(ла) резервні коди"
                  checked={codesSaved}
                  onChange={(event) => {
                    setCodesSaved(event.currentTarget.checked);
                  }}
                  data-testid="totp-ack"
                />
                <Button
                  fullWidth
                  disabled={!codesSaved}
                  data-testid="totp-continue"
                  onClick={() => {
                    setShowCodeForm(true);
                  }}
                >
                  Далі
                </Button>
              </>
            ) : (
              <form onSubmit={submitCode} noValidate>
                <Stack gap="sm">
                  <TextInput
                    label="Код з застосунку"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    data-testid="totp-code"
                    {...codeForm.getInputProps('code')}
                  />
                  {errorLine}
                  <Button type="submit" loading={busy} fullWidth data-testid="totp-code-submit">
                    Підтвердити
                  </Button>
                </Stack>
              </form>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
